import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { IAccountsPayable } from '@nkr/models/index';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import { Button } from 'react-bootstrap';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Utility } from '@nkr/services/utility';
import { ArStatusCell, arStatusValueGetter } from './ar-status-cell';

interface IProps {
    data: IAccountsPayable[],
    onGridReady: (e: GridReadyEvent) => void,
    onShowDetail: (centerId: number) => void
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const getTotals = (filteredList: IAccountsPayable[]): IAccountsPayable[] => {
        let totals = {
            centerId: 0,
            centerAlias: '',
            pending0To45: 0,
            pending46To60: 0,
            pending61To90: 0,
            pending91To120: 0,
            pending121To150: 0,
            pendingOver150: 0,
            totalPending: 0,
            payNow: 0,
            payAfterDayNumber: null,
            centerHasAchOnFile: false,
            centerHasOutstandingDelinquency: undefined,
            centerHasLatePaymentsInLastYear: false,
        };

        filteredList.forEach(item => {
            totals.pending0To45 += item.pending0To45;
            totals.pending46To60 += item.pending46To60;
            totals.pending61To90 += item.pending61To90;
            totals.pending91To120 += item.pending91To120;
            totals.pending121To150 += item.pending121To150;
            totals.pendingOver150 += item.pendingOver150;
            totals.totalPending += item.totalPending;
            totals.payNow += item.payNow;
        });

        return [totals];
    };

    const renderValue = (value: number) => {
        if (value == 0) {
            return <>-</>;
        }

        return <>{value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
    };

    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: 'Rank',
            headerClass: 'list-header',
            cellClass: 'list-cell-center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => params.data.centerId > 0 ? <RankRenderer {...params} /> : null,
            width: 50,
            suppressSizeToFit: true,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },

        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            headerClass: 'list-header',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            width: 155,
            cellClass: 'list-cell-center',
            sortable: true,
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => params.value == '' ? <>Totals</> : <>{params.value}</>
        },

        {
            colId: 'pending0To45',
            field: 'pending0To45',
            headerName: '0-45 Days',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>0-45 Days</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'pending46To60',
            field: 'pending46To60',
            headerName: '46-60 Days',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>46-60 Days</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'pending61To90',
            field: 'pending61To90',
            headerName: '61-90 Days',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>61-90 Days</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'pending91To120',
            field: 'pending91To120',
            headerName: '91-120 Days',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>91-120 Days</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'pending121To150',
            field: 'pending121To150',
            headerName: '121-150 Days',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>121-150 Days</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'pendingOver150',
            field: 'pendingOver150',
            headerName: '151+ Days',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>151+ Days</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'totalPending',
            field: 'totalPending',
            headerName: 'Total',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Total</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'payNow',
            field: 'payNow',
            headerName: 'Pay Now',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Pay Now</SortableHeaderComponent>,
            width: 140,
            cellClass: 'list-cell-right',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => renderValue(params.value)
        },

        {
            colId: 'centerHasLatePaymentsInLastYear',
            field: 'centerHasLatePaymentsInLastYear',
            headerName: 'AR Status',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>AR Status</SortableHeaderComponent>,
            width: 100,
            cellClass: 'list-cell-center',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: ArStatusCell,
            valueGetter: arStatusValueGetter
        },
        {
            field: 'statusSince',
            headerName: 'statusSince',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>AR Status Since</SortableHeaderComponent>,
            sortable: true,
            width: 100,
            cellClass: 'list-cell-center',
            comparator: Utility.dateComparator
        },

        {
            colId: 'centerHasAchOnFile',
            field: 'centerHasAchOnFile',
            headerName: 'ACH',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>ACH</SortableHeaderComponent>,
            width: 50,
            cellClass: 'list-cell-center',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            cellRendererFramework: (params: ICellRendererParams) => params.data.centerHasAchOnFile ? <span className='label label-success'>Y</span> : '-'
        },

        {
            colId: 'details',
            field: 'details',
            headerClass: 'list-header-right',
            headerComponentFramework: (params: IHeaderParams) => <></>,
            width: 80,
            cellClass: 'list-cell-center',
            sortable: true,
            sortingOrder: ['desc', 'asc'],
            resizable: false,
            // pinnedRowCellRendererFramework: () => <>&nbsp;</>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IAccountsPayable;

                if (data.centerId > 0) {
                    return (
                        <Button bsStyle='primary' bsSize='xsmall'
                            onClick={() => props.onShowDetail(params.data.centerId)}>
                            Details
                        </Button>
                    );
                }

                return null;
            },
        },
    ]

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);
    const totals = useMemo(() => getTotals(props.data), [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                columnDefs={columnDefs}
                defaultColDef={{
                    cellClassRules: {
                        'totalsBackground': (params) => {
                            return params.data.centerId == 0;
                        }
                    }
                }}
                rowData={rowData}
                headerHeight={30}
                groupHeaderHeight={30}
                rowHeight={30}
                pinnedTopRowData={totals}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                excelStyles={[
                    {
                        id: 'header',
                        alignment: {
                            horizontal: 'Center'
                        },
                        font: {
                            bold: true
                        }
                    },
                    {
                        id: 'cell',
                        alignment: {
                            horizontal: 'Center'
                        }
                    },
                    {
                        id: 'totalsBackground',
                        interior: {
                            color: '#ADD8E6',
                            pattern: 'Solid',
                            patternColor: 'black'
                        }
                    }
                ]}
                noRowsOverlayComponentFramework={() => <div>No records found...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={(e: SortChangedEvent) => { e.api.refreshCells(); }}
                onRowDataChanged={onRowDataChanged}
                getRowStyle={(props: any) => {
                    let data = props.data as IAccountsPayable;

                    if (data.centerId == 0) {
                        return { backgroundColor: 'lightblue', fontWeight: 700 } as any;
                    }
                }}
            >

            </AgGridReact>
        </ReportWrapper>
    );
}

export default Grid;